// Parallel run with Xero (M6 plan D5, D6): upload Xero's Account Transactions for a month and see
// every account side by side and the bank lines that do not match; record Xero's BAS for a quarter;
// explain each difference with a note.
import { useState } from 'react';
import type { Api } from '../api.js';
import { day, dollars } from '../format.js';
import { useOrgData } from './useOrgData.js';

interface Summary {
  id: string;
  kind: 'month' | 'bas';
  period_start: string;
  period_end: string;
  notes: number;
}
interface Line {
  date: string;
  description: string;
  amount: string;
}
interface Comparison {
  id: string;
  kind: 'month' | 'bas';
  periodStart: string;
  periodEnd: string;
  notes: { item: string; note: string; created_at: string }[];
  accounts?: { name: string; xero: string | null; foderis: string | null; difference: string }[];
  lines?: { account: string; matched: number; onlyInXero: Line[]; onlyInFoderis: Line[] }[];
  bas?: { label: string; foderis: string; xero: string; difference: string }[];
}

async function base64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export function XeroComparison({ api, orgId }: { api: Api; orgId: string }) {
  const list = useOrgData<{ comparisons: Summary[] }>(api, orgId, '/xero/comparisons');
  const [open, setOpen] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bas, setBas] = useState({ periodStart: '2026-10-01', g1: '', oneA: '', oneB: '' });

  async function upload(f: File | undefined) {
    if (!f) return;
    setBusy(true);
    setError('');
    try {
      const { id } = await api.send<{ id: string }>(orgId, 'POST', '/xero/comparisons', {
        file: await base64(f),
      });
      await list.reload();
      setOpen(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the file.');
    } finally {
      setBusy(false);
    }
  }
  async function recordBas() {
    setError('');
    try {
      const { id } = await api.send<{ id: string }>(orgId, 'POST', '/xero/bas', bas);
      await list.reload();
      setOpen(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record Xero's BAS.");
    }
  }

  return (
    <section className="stack">
      <h1>Comparison with Xero</h1>
      <p className="muted">
        Each month: in Xero, Reports, Account Transactions, all accounts, the month's dates, Update,
        Export, Excel. Upload it here.
      </p>
      <label className="button-like">
        {busy ? 'Reading' : "Upload Xero's Account Transactions (.xlsx)"}
        <input
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => void upload(e.target.files?.[0])}
        />
      </label>
      <div className="title-row wrap">
        <span>Xero's BAS for the quarter starting</span>
        <input
          type="date"
          value={bas.periodStart}
          onChange={(e) => setBas({ ...bas, periodStart: e.target.value })}
        />
        {(['g1', 'oneA', 'oneB'] as const).map((k) => (
          <input
            key={k}
            inputMode="decimal"
            placeholder={k === 'g1' ? 'G1' : k === 'oneA' ? '1A' : '1B'}
            value={bas[k]}
            onChange={(e) => setBas({ ...bas, [k]: e.target.value })}
          />
        ))}
        <button type="button" className="link" onClick={recordBas}>
          Compare
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      <table className="table">
        <tbody>
          {(list.data?.comparisons ?? []).map((c) => (
            <tr key={c.id}>
              <td>{c.kind === 'month' ? 'Month' : 'BAS'}</td>
              <td>
                {day(c.period_start)} to {day(c.period_end)}
              </td>
              <td className="muted">{c.notes ? `${c.notes} notes` : ''}</td>
              <td>
                <button
                  type="button"
                  className="link"
                  onClick={() => setOpen(open === c.id ? '' : c.id)}
                >
                  {open === c.id ? 'Hide' : 'Open'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <ComparisonView key={open} api={api} orgId={orgId} id={open} />}
      <XeroArchive api={api} orgId={orgId} />
    </section>
  );
}

function ComparisonView({ api, orgId, id }: { api: Api; orgId: string; id: string }) {
  const c = useOrgData<Comparison>(api, orgId, `/xero/comparisons/${id}`);
  const [item, setItem] = useState('');
  const [note, setNote] = useState('');
  if (!c.data) return <p className="muted">Comparing</p>;
  const d = c.data;
  const explained = new Set(d.notes.map((n) => n.item));
  async function save() {
    await api.send(orgId, 'POST', `/xero/comparisons/${id}/notes`, { item, note });
    setNote('');
    await c.reload();
  }
  const different = (d.accounts ?? []).filter((a) => a.difference !== '0.00');
  return (
    <div className="stack bank-card">
      {d.bas && (
        <table className="table">
          <thead>
            <tr>
              <th />
              <th className="num">Foderis</th>
              <th className="num">Xero</th>
              <th className="num">Difference</th>
            </tr>
          </thead>
          <tbody>
            {d.bas.map((b) => (
              <tr key={b.label} className={b.difference === '0.00' ? undefined : 'error'}>
                <td>{b.label}</td>
                <td className="num">{dollars(b.foderis)}</td>
                <td className="num">{dollars(b.xero)}</td>
                <td className="num">{dollars(b.difference)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {d.lines?.map((l) => (
        <div key={l.account} className="stack">
          <h2>
            {l.account}: {l.matched} lines match
          </h2>
          {[
            ...l.onlyInXero.map((x) => ['Only in Xero', x] as const),
            ...l.onlyInFoderis.map((x) => ['Only in Foderis', x] as const),
          ].map(([side, x]) => (
            <p key={`${side}-${x.date}-${x.amount}-${x.description}`} className="error">
              {side}: {day(x.date)} {x.description} {dollars(x.amount)}
            </p>
          ))}
        </div>
      ))}
      {d.accounts && (
        <table className="table">
          <caption>
            {different.length === 0
              ? 'Every account matches'
              : `${different.length} accounts differ`}
          </caption>
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">Xero</th>
              <th className="num">Foderis</th>
              <th className="num">Difference</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.accounts.map((a) => (
              <tr
                key={a.name}
                className={a.difference === '0.00' || explained.has(a.name) ? undefined : 'error'}
              >
                <td>{a.name}</td>
                <td className="num">{a.xero === null ? '' : dollars(a.xero)}</td>
                <td className="num">{a.foderis === null ? '' : dollars(a.foderis)}</td>
                <td className="num">{a.difference === '0.00' ? '' : dollars(a.difference)}</td>
                <td>
                  {a.difference !== '0.00' && (
                    <button type="button" className="link" onClick={() => setItem(a.name)}>
                      {explained.has(a.name) ? 'Explained' : 'Explain'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {d.notes.map((n) => (
        <p key={`${n.item}-${n.created_at}`} className="muted">
          {n.item}: {n.note}
        </p>
      ))}
      <div className="title-row wrap">
        <input
          placeholder="Account or figure"
          value={item}
          onChange={(e) => setItem(e.target.value)}
        />
        <input
          className="grow"
          placeholder="What explains the difference"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button type="button" className="link" disabled={!item || !note} onClick={save}>
          Save note
        </button>
      </div>
    </div>
  );
}

interface ArchiveFile {
  id: string;
  name: string;
  size_bytes: string;
  sha256: string;
  uploaded_at: string;
}

/** Everything exported from Xero before cancelling it (M6 plan D7), kept for seven years. */
export function XeroArchive({ api, orgId }: { api: Api; orgId: string }) {
  const list = useOrgData<{ files: ArchiveFile[] }>(api, orgId, '/xero/archive');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  async function upload(files: FileList | null) {
    if (!files) return;
    setError('');
    try {
      let n = 0;
      for (const f of Array.from(files)) {
        setBusy(`Uploading ${++n} of ${files.length}`);
        await api.send(orgId, 'POST', '/xero/archive', {
          name: f.name,
          contentType: f.type || 'application/octet-stream',
          file: await base64(f),
        });
      }
      await list.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload the file.');
    } finally {
      setBusy('');
    }
  }
  async function view(id: string) {
    const tab = window.open('', '_blank');
    try {
      const { url } = await api.get<{ url: string }>(orgId, `/xero/archive/${id}/file`);
      if (tab) tab.location.href = url;
    } catch {
      tab?.close();
    }
  }
  return (
    <section className="stack">
      <h2>Xero archive</h2>
      <p className="muted">
        Before cancelling Xero, export the profit and loss, balance sheet, trial balance and general
        ledger for every financial year, the contacts, the chart of accounts and the attached files,
        and upload them here. They are kept for seven years and cannot be changed or deleted.
      </p>
      <label className="button-like">
        {busy || 'Upload files to the archive'}
        <input
          type="file"
          multiple
          hidden
          disabled={!!busy}
          onChange={(e) => void upload(e.target.files)}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <table className="table">
        <tbody>
          {(list.data?.files ?? []).map((f) => (
            <tr key={f.id}>
              <td>{f.name}</td>
              <td className="num muted">
                {Math.max(1, Math.round(Number(f.size_bytes) / 1024))} KB
              </td>
              <td className="muted">{f.uploaded_at.slice(0, 10)}</td>
              <td>
                <button type="button" className="link" onClick={() => view(f.id)}>
                  Download
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
